import { useCallback, useState } from 'react';

/* ── Types ── */

export interface TranscriptEntry {
  id: number;
  text: string;
  timestamp: Date;
  isFinal: boolean;
  /** Participant identity (e.g. doctor or patient) */
  speaker: string;
}

export interface UseTranscriptionReturn {
  /** Whether transcription is actively receiving data */
  isListening: boolean;
  /** All transcript entries (final segments) */
  transcript: TranscriptEntry[];
  /** The current interim (not-yet-finalized) text */
  interimText: string;
  /** Speaker of the current interim text */
  interimSpeaker: string;
  /** Add a finalized segment (called by TranscriptionCollector) */
  addSegment: (entry: TranscriptEntry) => void;
  /** Load previously persisted segments (for recovery after refresh) */
  loadSegments: (entries: TranscriptEntry[]) => void;
  /** Update interim text (called by TranscriptionCollector) */
  setInterim: (text: string, speaker: string) => void;
  /** Update listening state (called by TranscriptionCollector) */
  setListening: (active: boolean) => void;
  /** Clear all transcript entries */
  clear: () => void;
  /** Get full transcript as plain text */
  getFullText: () => string;
}

/* ── Hook ── */

export function useTranscription(): UseTranscriptionReturn {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [interimText, setInterimText] = useState('');
  const [interimSpeaker, setInterimSpeaker] = useState('');

  const addSegment = useCallback((entry: TranscriptEntry) => {
    setTranscript((prev) => {
      // Dedup: skip if same id already exists OR same text+speaker within last 3 seconds
      if (prev.some(e => e.id === entry.id)) return prev;
      const recent = prev.slice(-5);
      const isDup = recent.some(e =>
        e.speaker === entry.speaker &&
        e.text === entry.text &&
        Math.abs(
          (e.timestamp instanceof Date ? e.timestamp.getTime() : 0) -
          (entry.timestamp instanceof Date ? entry.timestamp.getTime() : 0)
        ) < 3000
      );
      if (isDup) return prev;
      return [...prev, entry];
    });
  }, []);

  const loadSegments = useCallback((entries: TranscriptEntry[]) => {
    setTranscript((prev) => {
      const existingIds = new Set(prev.map(e => e.id));
      const newEntries = entries.filter(e => !existingIds.has(e.id));
      if (newEntries.length === 0) return prev;
      return [...newEntries, ...prev].sort((a, b) => a.id - b.id);
    });
  }, []);

  const setInterim = useCallback((text: string, speaker: string) => {
    setInterimText(text);
    setInterimSpeaker(speaker);
  }, []);

  const setListening = useCallback((active: boolean) => {
    setIsListening(active);
  }, []);

  const clear = useCallback(() => {
    setTranscript([]);
    setInterimText('');
    setInterimSpeaker('');
  }, []);

  const getFullText = useCallback(() => {
    return transcript
      .filter((e) => e.isFinal)
      .map((e) => `[${e.speaker}]: ${e.text}`)
      .join('\n');
  }, [transcript]);

  return {
    isListening,
    transcript,
    interimText,
    interimSpeaker,
    addSegment,
    loadSegments,
    setInterim,
    setListening,
    clear,
    getFullText,
  };
}
